import { graphql, DataValue } from '@apollo/client/react/hoc';
import * as React from 'react';
import { isNil } from 'lodash';

import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import Group from 'entities/Group';
import GroupCreationLimitNotice from './GroupCreationLimitNotice';

import USER_PRODUCT_LIMITS_QUERY from 'groups/queries/UserProductLimitsForOrgQuery.gql';
import type { UserProductLimitsForOrg } from 'groups/queries/__generated__/UserProductLimitsForOrg';
import Organization from 'entities/Organization';
import { LoadUserOrganizationsForGroupCreateOrEdit_me_organizations as LoadUserOrganization } from 'organizations/queries/__generated__/LoadUserOrganizationsForGroupCreateOrEdit';

export type Props = {
  selectedOrgUuid: string | undefined | null;
  orgOptionSelected: boolean;
  group: Group | undefined | null;
  data?: DataValue<UserProductLimitsForOrg>;
};

class GroupCreationLimitNoticeContainer extends React.Component<Props> {
  render() {
    const currentUser = CurrentUser.isSignedIn() && CurrentUser.getUser();
    const hasFreeProductLimitFeature =
      currentUser && currentUser.hasFeature('free_product_limits.web.apr-19');
    if (!hasFreeProductLimitFeature) {
      return null;
    }

    if (!this.props.orgOptionSelected) {
      return null;
    }

    const orgIsEntity = (org: Organization | LoadUserOrganization) => {
      return org instanceof Organization;
    };

    const { data, group, selectedOrgUuid } = this.props;
    const org = group && group.getOrganization();
    // @ts-ignore, type handled by above "orgIsEntity" check)
    const existingOrgAssociation = org && (orgIsEntity(org) ? org.get('uuid') : org.uuid);
    const groupHasOrg = !(isNil(existingOrgAssociation) && isNil(selectedOrgUuid));
    const userHasChangedOrg = groupHasOrg && existingOrgAssociation !== selectedOrgUuid;
    const { numberOfClassesLimit, numberOfRemainingClasses, willExceedNumberOfClassesLimit } =
      data?.productLimits?.userProductLimits ?? {};

    if (data?.loading || !numberOfClassesLimit) {
      return null;
    }

    if (data?.error) {
      return (
        <ErrorReporter error="GroupCreationLimitNoticeContainer - failed to fetch userProductLimits" />
      );
    }

    if (group && !userHasChangedOrg) {
      // if the user is editing an existing group and has not modified the org association,
      // show warning based on whether the group currently has limited send functionality
      return (
        <GroupCreationLimitNotice
          showWarning={group.get('hasLimitedSendFunctionality')}
          numberOfClassesLimit={numberOfClassesLimit}
          numberOfRemainingClasses={numberOfRemainingClasses ?? 0}
          isExistingGroup
        />
      );
    }

    // otherwise, show the warning based on GQL product limits data using the newly
    // selected org association
    return (
      <GroupCreationLimitNotice
        showWarning={willExceedNumberOfClassesLimit}
        numberOfClassesLimit={numberOfClassesLimit}
        numberOfRemainingClasses={numberOfRemainingClasses ?? 0}
        isExistingGroup={Boolean(group)}
      />
    );
  }
}

export { GroupCreationLimitNoticeContainer };

const withLimits = graphql<Props>(USER_PRODUCT_LIMITS_QUERY, {
  options: ({ selectedOrgUuid }) => ({
    variables: { orgUuid: selectedOrgUuid },
    fetchPolicy: 'cache-and-network',
  }),
});

// @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'typeof GroupCreationLimitNoticeC... Remove this comment to see the full error message
export default withLimits(GroupCreationLimitNoticeContainer);
